import React, { useState, useMemo, useEffect, FC } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
// mui components
import { Typography, Grid, Avatar } from '@mui/material';
// components
import { ActivityButtonPostItem } from './components/activity-button-post-item';
// custom hooks
import { useSidePanel } from 'hooks/useSidePanel';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
// slices
import { setCommentsArgs } from 'redux-store/slices/query-args-slice';
// selectors
import { queryArgsSelector } from 'redux-store/selector';
// api
import { useGetProjectsQuery } from 'redux-store/api/projects';
import {
  useCreateMessagesViewsBulkMutation,
  useGetRecentActivityQuery,
} from 'redux-store/api/activity-feed';
// utils
import { getTimeSpend } from 'utils/helpers';
import { getImageBySize } from 'utils/helpers';
// icons
import NotificationsMessage from 'assets/icons/Comment_new_icon.svg';
// styles
import { styles } from './styles';
import { Badge } from '../components/badge';

interface ActivityButtonProps {
  projectId: number;
  unviewedItemsCount?: number;
  handleSeeMore?: () => void;
}

export const ActivityButton = ({
  projectId,
  unviewedItemsCount,
  handleSeeMore,
}: ActivityButtonProps) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const { projects: projectsArgs } = useAppSelector(queryArgsSelector);

  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const activityButtonId = open ? 'activity-button-popover' : undefined;

  const [bulkUpdate] = useCreateMessagesViewsBulkMutation();
  const { data: projectsData } = useGetProjectsQuery(projectsArgs);
  const { data, isLoading } = useGetRecentActivityQuery(
    {
      projectId: Number(projectId),
      limit: 10,
      cursor,
    },
    { skip: !open }
  );

  const hasData = data?.items?.length > 0;

  const { handleOpenActivityFeed, setProjectId: setProjectIdCtx } = useSidePanel();

  const unviewedCount = useMemo(
    () => (typeof unviewedItemsCount === 'number' ? unviewedItemsCount : 0),
    [unviewedItemsCount]
  );

  useEffect(() => {
    if (open) {
      setProjectIdCtx(projectId);
    }
  }, [projectId, open]);

  const onOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    if (unviewedCount > 0) {
      markNotificationsAsViewed();
    }
    setCursor(undefined);
    setAnchorEl(null);
  };

  const getMore = () => {
    if (data?.meta?.nextCursor) {
      setCursor(data.meta.nextCursor);
    }
  };

  const onSeeMore = () => {
    onClose();
    handleSeeMore ? handleSeeMore() : handleOpenActivityFeed();
  };

  const markNotificationsAsViewed = () => {
    const unviewedCountArgs =
      location.pathname.includes('account-dashboard') && projectsData?.items?.length
        ? projectsData.items.map(({ id }) => id)
        : undefined;

    bulkUpdate({
      projectId: Number(projectId),
      unviewedCountArgs,
    });
  };

  const handleOpenPostModal = (postId: number) => {
    dispatch(setCommentsArgs({ cursor: undefined }));
    searchParams.set('postId', postId.toString());
    searchParams.set('projectId', projectId.toString());
    setSearchParams(searchParams);
  };

  const isNoContentVisible = !hasData && open && !isLoading;
  const isLoadingVisible = !hasData && open && isLoading;
  const isContentVisible = hasData && open;

  return (
    <Badge
      onOpen={onOpen}
      onClose={onClose}
      open={open}
      id={activityButtonId}
      popoverProps={{ anchorEl }}
      buttonProps={{ title: `${unviewedCount}`, icon: <NotificationsMessage /> }}
    >
      <Badge.Header heading="Recent Activity" actionText="See more" onActionClick={onSeeMore} />
      <Badge.Container additionalSx={styles.badgeContainer}>
        {isNoContentVisible && (
          <Badge.NoContent heading="No Activity" subheading="Looks like you have no activity yet" />
        )}
        {isLoadingVisible && <Badge.Loading />}
        {isContentVisible && (
          <Badge.InfiniteScroll
            dataLength={data?.items?.length}
            hasMore={!!data?.meta && !!data?.meta?.nextCursor}
            getMore={getMore}
          >
            {data.items.map((post) => {
              const avatar =
                post.type === 'COMMENT'
                  ? post.data?.comment?.profile?.avatar
                  : post.data.profile.avatar;
              const profile =
                post.type === 'COMMENT' ? post.data?.comment?.profile : post.data.profile;

              return (
                <Grid key={post.id} sx={styles.paperItem}>
                  <Grid sx={styles.contentContainer}>
                    <Grid sx={styles.postContainer}>
                      <Grid sx={styles.customAvatarContainer}>
                        {avatar ? (
                          <>
                            {!post.isViewed ? <Grid sx={styles.imageGreenDot}></Grid> : null}
                            <Avatar
                              alt="avatar"
                              sx={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                              }}
                              src={getImageBySize(avatar, 24, 24)}
                              variant="square"
                            />
                          </>
                        ) : (
                          <>
                            {!post.isViewed ? <Grid sx={styles.imageGreenDot}></Grid> : null}
                            <Grid sx={styles.avatarContainer}>
                              <Typography
                                sx={styles.avatar}
                              >{`${profile?.firstName[0]}${profile?.lastName[0]}`}</Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>

                      <ActivityButtonPostItem
                        post={post}
                        handleOpenPostModal={handleOpenPostModal}
                      />
                    </Grid>
                  </Grid>

                  <Grid sx={styles.dateContainer}>
                    <Typography sx={styles.dateText}>
                      {getTimeSpend(formatDistanceToNowStrict(new Date(post.createdAt)))}
                    </Typography>
                  </Grid>
                </Grid>
              );
            })}
          </Badge.InfiniteScroll>
        )}
      </Badge.Container>
    </Badge>
  );
};
